//Base Event Emitter Package
//The goal of this SIP Class is Basically For It To Be A Glorified Event Emitter and Session Handler.
import { EventEmitter } from 'events';

//SipJS Modules
import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
  UserAgentState,
  RequestPendingError
} from 'sip.js';


//Extended Session Description Handler
import {SessionDescriptionHandlerFactory} from "./SessionDescriptionHandlerFactory";

export default class VOXOSip extends EventEmitter {

  constructor(options = {}) {
    super();

    //Instantiate Sessions Array
    this.sessions = [];

    //Copy Initial Options
    this.options = {...options};

    //Config For Session Expiration
    this.sessionExpires = 120;

    //Keep Alive Config
    this.keepAliveInterval = 25;

    //Feathers Client For API Related SoftphoneFunctionality
    this.feathersClient = options.feathersClient;

    //Resilience Tuning (all values in seconds)
    this.reconnectionDelay = options.reconnectionDelay || 2;
    this.maxReconnectionDelay = options.maxReconnectionDelay || 30;
    this.registrationTimeout = options.registrationTimeout || 8;
    this.registrationRetryDelay = options.registrationRetryDelay || 60;
    this.wakeProbeFreshness = options.wakeProbeFreshness || 30;
    this.healthCheckInterval = options.healthCheckInterval || 10;
    this.sleepGapThreshold = options.sleepGapThreshold || 120;
    this.rebuildAfterAttempts = options.rebuildAfterAttempts || 6;
    this.transportCloseTimeout = options.transportCloseTimeout || 5;
    this.rebuildDeferralLimit = options.rebuildDeferralLimit || 60;

    //Multiple of the granted registration expiry we tolerate before calling it stale
    this.staleRegistrationFactor = options.staleRegistrationFactor || 2;

    //Create The User Agent From Sip Address
    this.userAgent = this.configureUserAgent(options);

    //Initialize Connection Related Status Properties
    this.connectRequested = false;
    this.disconnectRequested = false;
    this.attemptingReconnection = false;
    this.disposed = false;

    //Initialize registrar property
    this.registerer = undefined;

    //Registration intent, held here so every recovery pass re-registers without
    //being asked again. Only unregister() clears it.
    this.registrationRequested = false;
    this.registererOptions = {};
    this.registererRegisterOptions = {};
    this.registrationVerifiedAt = 0;
    this.registrationBlockedUntil = 0;

    //Initial reconnecting property
    this.reconnecting = false;

    //Initial online / offline status
    this.offline = false;

    //Recovery coalescing. Only one recovery loop is ever allowed to run.
    this.recoveryPromise = null;
    this.queuedRecoveryPromise = null;
    this.recycleTransportOnNextRecovery = false;
    this.recyclingTransport = false;
    this.rebuildDeferredSince = 0;
    this.userAgentStarted = false;
    this.wakeDebounceTimer = null;

    this.onlineCallback = this.onlineEventLogic.bind(this);
    this.offlineCallback = this.offlineEventLogic.bind(this);
    this.wakeCallback = this.wakeEventLogic.bind(this);
    this.healthCheckTimer = null;

    this.bindEnvironmentListeners();
    this.startHealthCheck();

    //Quick Transfer is active flag, defaults to false
    this._quickTransferActive = false;
  }

  //Every one of these hints the socket we think we have may already be gone
  bindEnvironmentListeners() {
    window.addEventListener('online', this.onlineCallback);
    window.addEventListener('offline', this.offlineCallback);

    //tab / window / machine woke back up
    document.addEventListener('visibilitychange', this.wakeCallback);
    window.addEventListener('focus', this.wakeCallback);
    window.addEventListener('pageshow', this.wakeCallback);
  }

  unbindEnvironmentListeners() {
    window.removeEventListener('online', this.onlineCallback);
    window.removeEventListener('offline', this.offlineCallback);
    document.removeEventListener('visibilitychange', this.wakeCallback);
    window.removeEventListener('focus', this.wakeCallback);
    window.removeEventListener('pageshow', this.wakeCallback);
  }

  //Watchdog. Verifies the connection is usable and doubles as a wake detector -
  //timers stall while suspended, so a large gap between ticks means a dead socket.
  startHealthCheck() {
    this.stopHealthCheck();
    this.lastHealthCheck = Date.now();
    this.healthCheckTimer = setInterval(() => this.healthCheck(), this.healthCheckInterval * 1000);
  }

  stopHealthCheck() {
    if(this.healthCheckTimer){
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  // Used a getter and setter here because for some reason, the user agent configure would never get set to true correctly on quick transfer logic
  // This will allow us to hide the invitation modal in the invitation callback
  get quickTransferActive() {
    return this._quickTransferActive;
  }

  set quickTransferActive(val) {
    if (val) {
      this._quickTransferActive = val;
    }
  }

  //logic to follow for online event
  onlineEventLogic() {
    console.log('VOXOSip: the browser came back online');
    //Nothing survives losing the network, so do not try to reuse the old socket.
    this.recycleTransportOnNextRecovery = true;
    this.ensureHealthy('browser-online');
  }

  //logic to follow for offline event
  offlineEventLogic() {
    console.log('VOXOSip: the browser went offline');
    if(!this.offline){
      this.emit('offline');
      this.offline = true;
    }
    //Nothing to do until the network is back, but the next recovery needs a new socket
    this.recycleTransportOnNextRecovery = true;
  }

  //Tab became visible, window regained focus, or the page came back from the
  //back/forward cache. Any of those can happen long after the socket quietly
  //died, and the browser will not tell us, so check for ourselves.
  wakeEventLogic() {
    if(document.visibilityState && document.visibilityState !== 'visible'){
      return;
    }

    //visibilitychange, focus and pageshow all tend to fire for a single wake up
    if(this.wakeDebounceTimer){
      return;
    }

    //Do not probe on every alt-tab: a REGISTER tells us nothing the registrar
    //confirmed moments ago, and the watchdog is only seconds behind us anyway.
    const registrationAge = Date.now() - this.registrationVerifiedAt;
    if(this.isConnected() && this.registrationVerifiedAt && registrationAge < this.wakeProbeFreshness * 1000){
      return;
    }

    this.wakeDebounceTimer = setTimeout(() => {this.wakeDebounceTimer = null}, 1000);
    this.ensureHealthy('wake');
  }

  //Generate Unique ID As Session Identifier
  generateUniqueId() {
    return Math.random().toString(36).substr(2, 9);
  }

  //Set Up The User Agent For This Instance
  configureUserAgent(options) {

    //Parse Address From Options
    const userAgentOptions = {...options.provisionOptions};
    userAgentOptions.uri = UserAgent.makeURI(options.sipAddress);
    if(!userAgentOptions.uri){
      throw new Error('No Address of Record Was Specified: FROM configureUserAgent()');
    }

    //User Agent Name
    userAgentOptions.userAgentString = 'VOXO Desktop 1.51'

    //Setting Up Transport Server Array
    userAgentOptions.transportOptions = {server: options.server, traceSip: !!options.logging, keepAliveInterval: this.keepAliveInterval};
    userAgentOptions.logBuiltinEnabled = !!options.logging;

    //Assigned at the bottom of this method and captured by the delegates below. A
    //replaced user agent can still report events long afterwards (stop() waits on a
    //close handshake a dead socket may never answer); acting on those would tear
    //down the healthy connection that took its place.
    let agent;
    const isCurrentAgent = () => this.userAgent === agent;

    //Handling For User Agent Connect, Disconnect, and Invite
    userAgentOptions.delegate = {

      onConnect: () => {
        if(!isCurrentAgent()) return;

        this.emit('connected');

        //A registration never survives a new socket. A running recovery pass
        //re-registers for us; a transport that came back on its own does not, and
        //without this the softphone sits connected but unreachable for inbound calls.
        if(this.registrationRequested && !this.recoveryPromise){
          this.ensureHealthy('transport-connect');
        }
      },
      onDisconnect: (error) => {

        //Do not fight a shutdown we asked for: a close we initiated comes back
        //through here too, and treating it as a fresh failure would have us
        //recycling the healthy socket we are in the middle of building.
        if(!isCurrentAgent() || this.disconnectRequested || this.recyclingTransport || !this.connectRequested){
          return;
        }

        if(!this.offline){
          this.emit('offline');
          this.offline = true;
        }

        //Do not hang up what is still up. session.bye() marks the session Terminated
        //whether or not the BYE reaches the wire, and sip.js closes the peer
        //connection on that transition - so a call whose media was fine goes silent
        //the moment the socket drops, and with no BYE delivered the far end holds the
        //leg open hearing nothing. Media does not ride this socket; leave the calls
        //alone and let recovery bring signalling back. If it cannot, the rebuild ends
        //them through abandonSessions().

        //The socket is gone whether or not sip.js handed us an error - a firewall,
        //proxy or load balancer dropping an idle flow reports a clean close.
        console.log('VOXOSip: transport disconnected', error && error.message);
        this.recycleTransportOnNextRecovery = true;
        this.ensureHealthy('transport-disconnect');
      },
      onInvite: (invitation) => {
        if(!isCurrentAgent()) return;

        if (!this.quickTransferActive) {
          //Configure Session On The Instance
          const autoHangup = this.configureSession(invitation, 'incoming');

          if(autoHangup){
            invitation.reject();
            return;
          }

          //Emit Instance Invite Event
          this.emit('invite', invitation);

        }

        this._quickTransferActive = false;
      },
    }

    //Set Up A Custom Session Description Handler For This User Agent
    userAgentOptions.sessionDescriptionHandlerFactory = SessionDescriptionHandlerFactory();

    agent = this.createUserAgent(userAgentOptions);
    return agent;
  }

  //The only place a sip.js UserAgent is constructed, kept separate so the recovery
  //logic can be exercised against a transport we control.
  createUserAgent(userAgentOptions) {
    return new UserAgent(userAgentOptions);
  }

  //The only place a sip.js Registerer is constructed. See createUserAgent().
  createRegisterer(registererOptions) {
    return new Registerer(this.userAgent, registererOptions);
  }

  //Set Up Session On The Instance
  configureSession(session, direction, outboundRecord) {

    //Generate A Unique ID For The Session
    session.uniqueId = this.generateUniqueId();

    //Assign Session Direction and Hold Prop
    session.direction = direction;
    session.muted = false;
    session.held = false;

    //Generate Simple Caller ID Keys
    const { remoteIdentity } = session;
    session.cidName = remoteIdentity.displayName;
    session.cidNum = remoteIdentity.uri.user;

    //Parse Special Headers For Blind Transfer Information
    session.transerrerExten = null;
    const {headers} = session.request;

    const {Bxfer, Xrec} = headers;

    //check auto answer for page inbound
    //we basically just auto reject if this is a page
    const answerMode = headers['Answer-Mode'];
    const autoHangup = answerMode && answerMode[0].raw === 'Auto'

    if(Bxfer){
      const splitDialString = Bxfer[0].raw.split('/')[1];
      session.transferrerExten = splitDialString.split('-')[0];
    }

    // set up state of recording based on direction of call
    if(direction === 'outgoing'){
      session.recording = outboundRecord === "yes" || outboundRecord === "yeschange";
      session.canRecord = session.recording || outboundRecord === "nochange";
      session.callId = headers['Call-ID'][0];
    }

    if(direction === 'incoming'){

      session.canRecord = !!Xrec;

      if(Xrec){
        const recordingState = +Xrec[0].raw;
        session.recording = !!recordingState;
      }

      session.callId = headers.Xcid[0].raw;

    }

    //Set up session delegate
    session.delegate = {
      onInvite: (invitation) => {
        this.emit('inviteUpdate', {...invitation, uniqueId: session.uniqueId} )
      }
    }

    //Set Up The Session On The Instance
    this.sessions.push(session);

    //Session State and Media Handling
    session.stateChange.addListener((state) => {
      switch (state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          break;
        case SessionState.Established:
          this.setupLocalMedia(session.uniqueId);
          this.setupRemoteMedia(session.uniqueId);
          break;
        case SessionState.Terminating:
        case SessionState.Terminated:
          if(!autoHangup){
            this.emit('hangup', session);
          }
          this.removeSession(session.uniqueId);
          this.cleanupMedia();
          break;
        default:
          throw new Error("Unknown session state.");
      }
    });


    // console.log('answer mode', answerMode[0].raw);
    return autoHangup;


  }

  // ----------------------------------- MEDIA HANDLERS ------------------------------------ //

  /*
  The following items are responsible for capturing media
  streams and attaching them to the DOM Properly as well as manipulating
  sender tracks from the local user
  */

  //Attaches Media Stream To HTML Element For Local Stream
  setupLocalMedia(sessionId) {

    const session = this.findSession(sessionId);

    if (!session) {
      throw new Error("Session does not exist: FROM setupLocalMedia()");
    }

    const mediaElement = this.options.localStreamElement;
    if (mediaElement) {
      const localStream = this.localMediaStream(sessionId);
      if (!localStream) {
        throw new Error("Local media stream undefined.");
      }
      mediaElement.pause();
      mediaElement.srcObject = localStream;
      mediaElement.volume = 0;
      setTimeout(() => {
        mediaElement.play().catch((error) => {
          console.log(error.message);
        });
      }, 150)
    }
  }

  //Attaches Media Stream to HTML Element Fro Remote Stream
  setupRemoteMedia(sessionId) {

    const session = this.findSession(sessionId);

    if (!session) {
      throw new Error("Session does not exist: FROM setupRemoteMedia()");
    }

    const mediaElement = this.options.remoteStreamElement;

    if (mediaElement) {
      const remoteStream = this.remoteMediaStream(sessionId);
      if (!remoteStream) {
        throw new Error("Remote media stream undefined.");
      }
      mediaElement.autoplay = true;
      mediaElement.srcObject = remoteStream;
      setTimeout(() => {
        mediaElement.play().catch((error) => {
          console.log(error.message);
        });
      }, 150)
      remoteStream.onaddtrack = () => {
        mediaElement.load();
        setTimeout(() => {
          mediaElement.play().catch((error) => {
            console.log(error.message);
          });
        }, 150)
      };
    }
  }

  //Return the Local Media Stream (Undefined If Not Answered)
  localMediaStream(sessionId) {
    const session = this.findSession(sessionId);

    if (!session) {
      return undefined;
    }

    const sdh = session.sessionDescriptionHandler;

    if(sdh) {
      sdh.peerConnectionDelegate = {
        onconnectionstatechange: (event) => {
          const { connectionState } = event.currentTarget;
          if(connectionState === 'disconnected' || connectionState === 'failed'){
            console.log('lost rtp. trying to establish');
            //Nothing awaits this, and a re-INVITE on a dying call fails routinely -
            //losing the transport mid-call fires this repeatedly
            this.sendReinvite(sessionId).catch((error) => {
              console.log('could not re-invite after losing rtp -', error && error.message ? error.message : error);
            });
          }
        },
      }
      return sdh.localMediaStream;
    }

    return undefined;

  }

  //Return the Remote Media Stream (Undefined If Not Answered)
  remoteMediaStream(sessionId) {
    const session = this.findSession(sessionId);
    const sdh = session && session.sessionDescriptionHandler;
    return sdh ? sdh.remoteMediaStream : undefined;
  }

  //Enable and Disabled Media Tracks For A Session ID
  enableSenderTracks(enable, sessionId) {

    const session = this.findSession(sessionId);

    if (!session) {
      throw new Error("Session does not exist.");
    }

    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      throw new Error("Peer connection closed.");
    }

    peerConnection.getSenders().forEach((sender) => {
      if (sender.track) {
        sender.track.enabled = enable;
      }
    });
  }

  //Enable and Disable Media Tracks For A Session ID
  enableReceiverTracks(enable, sessionId) {

    const session = this.findSession(sessionId);

    if (!session) {
      throw new Error("Session does not exist.");
    }

    const sessionDescriptionHandler = session.sessionDescriptionHandler;
    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      throw new Error("Peer connection closed.");
    }

    peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        receiver.track.enabled = enable;
      }
    });
  }

  //Clean Up Media If No Sessions Are Left
  cleanupMedia(){
    if(!this.sessions.length) {
      if(this.options.localStreamElement){
        this.options.localStreamElement.srcObject = null;
        this.options.localStreamElement.pause();
      }
      if(this.options.remoteStreamElement) {
        this.options.remoteStreamElement.srcObject = null;
        this.options.remoteStreamElement.pause();
      }
    }
  }

  // --------------------------------------------------------------------------------------- //

  // ---------------------------------------- HELPER FUNCTIONS ----------------------------- //

  //Find A Particular Session By Unique ID
  findSession(sessionId) {
    return this.sessions.find(item => item.uniqueId === sessionId);
  }

  //A session the user placed or answered. An unanswered inbound invitation sits
  //in Initial - it is not theirs to lose yet.
  hasActiveCall() {
    return this.sessions.some(session => session.state !== SessionState.Initial);
  }

  //Get the cidNum for a given session
  getCidNum(sessionId) {
    const session = this.findSession(sessionId);
    return session ? session.cidNum : '';
  }

  //Remove A Terminated Session From Session Array
  removeSession(sessionId) {
    this.sessions = this.sessions.filter(item => item.uniqueId !== sessionId);
  }

  //Register The User Agent For SIP Events. The request is remembered on the
  //instance, so any later reconnect puts the registration back automatically.
  //Resolves once the registrar has accepted us, and keeps retrying until it does.
  async register(registererOptions = {}, registererRegisterOptions = {}){
    //Only a genuinely new request clears an active backoff. The app re-asks on
    //every reconnect, and letting a re-ask reset the timer would turn a rejecting
    //registrar into a tight retry loop.
    if(!this.registrationRequested){
      this.registrationBlockedUntil = 0;
    }

    this.registrationRequested = true;
    this.registererOptions = {...registererOptions};
    this.registererRegisterOptions = {...registererRegisterOptions};

    return this.ensureHealthy('register');
  }

  //Unregister and Stop Receiving SIP Events
  async unregister(registererUnregisterOptions) {
    //Explicit intent: stop putting ourselves back in service on every recovery.
    this.registrationRequested = false;
    this.registrationBlockedUntil = 0;
    return this.registerer ? await this.registerer.unregister(registererUnregisterOptions) : Promise.resolve();
  }

  //Connect The User Agent. Resolves once the transport is really up, retrying with
  //backoff rather than failing the caller, so the softphone still comes up if the
  //first attempt lands while the network is down.
  async connect() {
    //Revive an instance that was disconnected - disconnect() tears down the watchdog
    //and the browser listeners, and a stale 'disposed' makes every recovery a no-op.
    if(this.disposed){
      this.disposed = false;
      this.bindEnvironmentListeners();
      this.startHealthCheck();
    }

    this.connectRequested = true;
    this.disconnectRequested = false;
    this.lastHealthCheck = Date.now();
    return this.ensureHealthy('connect');
  }

  //Disconnect The User Agent. connect() can be used afterwards to start over.
  async disconnect() {
    this.disposed = true;
    this.disconnectRequested = true;
    this.connectRequested = false;
    this.registrationRequested = false;

    this.stopHealthCheck();
    clearTimeout(this.wakeDebounceTimer);
    this.wakeDebounceTimer = null;

    this.unbindEnvironmentListeners();

    return await this.userAgent.stop();
  }

  //Create Invitation and Make A Call
  async call(destination, inviterOptions = {},emergencyDetails = {}, inviterInviteOptions = {}, outboundRecord) {



    //Create Target Address
    const target = UserAgent.makeURI(destination);
    if (!target) {
      return Promise.reject(new Error(`Failed to create a valid URI from "${destination}"`));
    }

    //add x-loc headers for emergency call
    const { isEmergency, userLocation } = emergencyDetails;
    const locationAvailable  = userLocation.lat && userLocation.long;
    if(isEmergency && locationAvailable){
      inviterOptions.extraHeaders = [
        `X-Location: geo:${userLocation.lat},${userLocation.long}`
      ];
    }

    // Use our configured constraints as InvitationAcceptOptions if none provided
    if (!inviterOptions.sessionDescriptionHandlerOptions) {
      inviterOptions.sessionDescriptionHandlerOptions = {};
    }
    if (!inviterOptions.sessionDescriptionHandlerOptions.constraints) {
      inviterOptions.sessionDescriptionHandlerOptions.constraints = this.constraints;
    }

    //Hook Into The On Accept Method to Parse Additional Headers (We Hope)
    inviterInviteOptions.requestDelegate = {
      onAccept: (message) => {
        const session = this.sessions.find(item => item.fromTag === message.message.fromTag);
        this.emit('accepted', session);
      },
      onTrying: (message) => {
        const session = this.sessions.find(item => item.fromTag === message.message.fromTag)
        this.emit('trying', session);
      },
      onReject: (message) => {
        const {message: incomingMessage} = message;
        if(incomingMessage){
          const {statusCode} = incomingMessage;
          if(statusCode === 500){
            this.emit('callFailed');
          }
        }
      }
    }

    // Create a new Inviter for the outgoing Session
    const inviter = new Inviter(this.userAgent, target, inviterOptions);

    // Send INVITE
    return await this.sendInvite(inviter, inviterOptions, inviterInviteOptions, outboundRecord);
  }

  //Request Hold For A Specified Session ID
  async hold(sessionId){
    await this.setHold(true, sessionId);
  }

  //Request Unhold For A Specified Session ID
  async unhold(sessionId){
    await this.setHold(false, sessionId);
  }

  //Put Every Call On Hold Excluding the Specified Session ID
  async holdOtherCalls(sessionId){
    const sessions = this.sessions.filter(item => item.uniqueId !== sessionId);
    const ignore = ['Initial', 'Establishing'];
    for(let i = 0; i < sessions.length; i++){
      if(!sessions[i].held && !ignore.includes(sessions[i].state)){
        await this.hold(sessions[i].uniqueId);
      }
    }
  }

  //Mute Local Sender Audio For A Specified Session ID
  mute(sessionId) {
    this.setMute(true, sessionId);
  }

  //Unmute Local Sender Audio For A Specified Session Id
  unmute(sessionId){
    this.setMute(false, sessionId);
  }

  //Helper to See if User Agent is Currently Connected
  isConnected() {
    try {
      return !!this.userAgent && this.userAgent.isConnected();
    } catch (error) {
      return false;
    }
  }

  //Helper to See if We Are Currently Registered With The Registrar
  isRegistered() {
    return !!this.registerer && this.registerer.state === RegistererState.Registered;
  }

  // --------------------------------------------------------------------------------------- //


  // ------------------------------------------- SESSION CONTROLLERS ----------------------- //

  /*
  Provides call handling functionality for an existing Session.
  Includes holding / and muting functionality
  */

  //Answer A Call For The Provided Session ID
  async answer(sessionId, invitationAcceptOptions = {}) {
    const session = this.findSession(sessionId);

    if(!session){
      return Promise.reject('Session does not exist: FROM answer()');
    }

    // Use our configured constraints as InvitationAcceptOptions if none provided
    if (!invitationAcceptOptions.sessionDescriptionHandlerOptions) {
      invitationAcceptOptions.sessionDescriptionHandlerOptions = {};
    }

    if (!invitationAcceptOptions.sessionDescriptionHandlerOptions.constraints) {
      invitationAcceptOptions.sessionDescriptionHandlerOptions.constraints = this.constraints;
    }

    return session.accept(invitationAcceptOptions);
  }

  //Send an Outbound Invite
  async sendInvite (inviter, inviterOptions, inviterInviteOptions, outboundRecord) {
    this.configureSession(inviter, 'outgoing', outboundRecord);
    return await inviter.invite(inviterInviteOptions);
  }

  //Send A Re-Invite (Generic Use)
  async sendReinvite(sessionId) {

    console.log('sending the reinvite on session', sessionId);

    const session = this.findSession(sessionId);

    if(!session){
      return Promise.reject('Session does not exist: FROM Hangup()');
    }

    if(!session.held){


      const options = {
        requestDelegate: {
          onAccept: () => {
            session.held = false;
            this.enableReceiverTracks(true, sessionId);
            this.enableSenderTracks(true, sessionId);
          },
          onReject: () => {
            this.enableReceiverTracks(true, sessionId);
            this.enableSenderTracks(true, sessionId);
          }
        }
      };

      const sessionDescriptionHandlerOptions = session.sessionDescriptionHandlerOptionsReInvite;
      sessionDescriptionHandlerOptions.hold = false;
      session.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

      // Send re-INVITE
      return session
        .invite(options)
        .then(() => {
          // preemptively enable/disable tracks
          this.enableReceiverTracks(true, sessionId);
          this.enableSenderTracks(true, sessionId);
        })
        .catch((error) => {
          if (error instanceof RequestPendingError) {
            console.log('A reinvite request is already pending.');
          }
          throw error;
        });
    }
  }

  //Decline A Call For The Provided Session Id
  async decline(sessionId) {
    const session = this.findSession(sessionId);
    return session ? session.reject({statusCode: 603}) : Promise.reject('Session Does Not Exist: FROM decline()');
  }

  //Do Hangup For A Provided Session Id (Send BYE, REJECT, CANCEL)
  async hangup(sessionId) {

    const session = this.findSession(sessionId);

    if(!session){
      return Promise.reject('Session does not exist: FROM hangup()');
    }

    switch (session.state) {
      case SessionState.Initial:
        if (session instanceof Inviter) {
          return session.cancel();
        } else if (session instanceof Invitation) {
          return session.reject({statusCode: 603});
        } else {
          throw new Error("Unknown session type.");
        }
      case SessionState.Establishing:
        if (session instanceof Inviter) {
          return session.cancel();
        } else if (session instanceof Invitation) {
          return session.reject({statusCode: 603});
        } else {
          throw new Error("Unknown session type.");
        }
      case SessionState.Established:
        return session.bye();
      case SessionState.Terminating:
      case SessionState.Terminated:
        break;
      default:
        throw new Error("Unknown state");
    }

    return Promise.resolve();
  }

  //Handle Hold For A Session ID
  async setHold(hold, sessionId){

    const session = this.findSession(sessionId);

    if (!session) {
      return Promise.reject(new Error("Session does not exist."));
    }


    // Just resolve if we are already in correct state
    if (session.held === hold) {
      return Promise.resolve();
    }

    //If we are removed from hold, make sure audio streams get attached properly
    if(!hold){
      this.setupLocalMedia(sessionId);
      this.setupRemoteMedia(sessionId);
    }

    const options = {
      requestDelegate: {
        onAccept: () => {
          session.held = hold;
          this.enableReceiverTracks(!hold, sessionId);
          this.enableSenderTracks(!hold && !session.muted, sessionId);
        },
        onReject: () => {
          this.enableReceiverTracks(!hold, sessionId);
          this.enableSenderTracks(!hold && !session.muted, sessionId);
        }
      }
    };

    const sessionDescriptionHandlerOptions = session.sessionDescriptionHandlerOptionsReInvite;
    sessionDescriptionHandlerOptions.hold = hold;
    session.sessionDescriptionHandlerOptionsReInvite = sessionDescriptionHandlerOptions;

    // Send re-INVITE
    return session
      .invite(options)
      .then(() => {
        // preemptively enable/disable tracks
        this.enableReceiverTracks(!hold, sessionId);
        this.enableSenderTracks(!hold && !session.muted, sessionId);
      })
      .catch((error) => {
        if (error instanceof RequestPendingError) {
          console.log('A hold request is already in progress.');
        }
        throw error;
      });
  }

  //Handle Mute and Unmute For A Specified Session Id
  setMute(mute, sessionId) {
    const session = this.findSession(sessionId);

    if(!session) {
      throw new Error('Session Does Not Exist');
    }

    session.muted = mute;
    this.enableSenderTracks(!session.held && !session.muted, sessionId);
  }


  // --------------------------------------------------------------------------------------- //


  // ----------------------------------------- CONNECTION HANDLERS ------------------------- //


  /*
  The single entry point for "make sure this softphone is connected and in service".
  Every trigger - transport disconnect, browser online, tab wake up, watchdog tick,
  an explicit register() - funnels through here.

  Calls are coalesced: only one recovery loop runs, and anything asking while one is
  in flight gets a re-check queued for when it finishes.
  */
  ensureHealthy(reason = 'unknown') {

    //Nothing to keep alive if we were never asked to connect, or we are shut down
    if(this.disposed || !this.connectRequested){
      return Promise.resolve();
    }

    //A pass is already running, so the caller waits for it and then gets a pass of
    //its own: handing back the running promise could resolve a register() request
    //that the running pass had already moved past.
    if(this.recoveryPromise){
      if(!this.queuedRecoveryPromise){
        this.queuedRecoveryPromise = this.recoveryPromise.then(() => {
          this.queuedRecoveryPromise = null;
          return this.ensureHealthy('re-check');
        });
      }
      return this.queuedRecoveryPromise;
    }

    this.reconnecting = true;
    this.attemptingReconnection = true;

    //Deferred so recoveryPromise is always assigned before the loop can run
    this.recoveryPromise = Promise.resolve()
      .then(() => this.runRecovery(reason))
      .catch((error) => {
        console.log('VOXOSip: recovery aborted -', error && error.message);
      })
      .then(() => {
        this.recoveryPromise = null;
        this.reconnecting = false;
        this.attemptingReconnection = false;
      });

    return this.recoveryPromise;
  }

  /*
  Retry loop behind ensureHealthy, which never gives up while a connection is wanted:
  bring the transport up, then prove it works by getting a REGISTER answered. Either
  step failing throws the socket away and backs off exponentially, and after enough
  failures the user agent is replaced outright.
  */
  async runRecovery(reason) {

    let attempt = 0;

    while (!this.disposed && this.connectRequested) {

      attempt++;

      if(attempt > 1){
        const delay = this.backoffDelay(attempt);
        console.log(`VOXOSip: recovery (${reason}) attempt ${attempt} in ${delay.toFixed(1)}s`);
        await this.wait(delay);

        if(this.disposed || !this.connectRequested){
          return;
        }
      }

      const wasOffline = this.offline;

      try {

        //A socket that lived through a suspend, network drop or disconnect can look
        //open while being completely dead, so throw it away rather than trust it.
        if(this.recycleTransportOnNextRecovery){
          this.recycleTransportOnNextRecovery = false;
          await this.recycleTransport();
        }

        //Enough failed passes means the user agent itself is the problem
        if(attempt > 1 && attempt % this.rebuildAfterAttempts === 0 && !this.shouldDeferRebuild()){
          await this.rebuildUserAgent();
        }

        await this.ensureTransportConnected();

        if(this.registrationRequested && !this.registrationBlocked()){
          await this.confirmRegistration();

          //unregister() can land while the REGISTER is in flight. Honour it, or the
          //softphone ends up in service for someone who just switched it off.
          if(this.registrationRequested){
            //Only a REGISTER we actually got an answer to lifts the backoff
            this.registrationBlockedUntil = 0;
          } else {
            await this.unregister().catch(() => {console.log('VOXOSip: could not undo an unwanted registration')});
          }
        }

        this.offline = false;

        console.log(`VOXOSip: recovery (${reason}) succeeded on attempt ${attempt}`);

        if(wasOffline){
          this.emit('reconnected');
        }

        return;

      } catch (error) {

        console.log(`VOXOSip: recovery (${reason}) attempt ${attempt} failed -`, error && error.message);

        //A REGISTER the registrar answered with an error (wrong secret, extension
        //removed) will not start working two seconds from now, so back right off
        //instead of hammering it. The watchdog retries once the block expires.
        if(error && error.registrationRejected){
          this.registrationBlockedUntil = Date.now() + (this.registrationRetryDelay * 1000);

          //The transport is up and the registrar is refusing us, which is not a
          //network problem - clear offline first or the app goes on blaming the
          //network for what is really a bad secret.
          this.offline = false;
          if(wasOffline){
            this.emit('reconnected');
          }

          this.emit('registrationFailed', error);
          return;
        }

        //Signalling is broken - the next pass gets a brand new socket
        this.recycleTransportOnNextRecovery = true;
      }
    }
  }

  //Bring the transport up. sip.js does not reconnect on its own, and cannot restart
  //a user agent that has been stopped, so that case gets a new one.
  async ensureTransportConnected() {

    if(!this.userAgentStarted){
      //First time through: a freshly built user agent has never been started
      this.userAgentStarted = true;
      await this.userAgent.start();
    } else if(this.userAgent.state === UserAgentState.Stopped){
      //A user agent that has been stopped can never be restarted - replace it
      await this.rebuildUserAgent();
    } else if(!this.isConnected()) {
      try {
        await this.userAgent.reconnect();
      } catch (error) {
        //reconnect() refuses to run on a user agent that never finished starting
        await this.userAgent.start();
      }
    }

    if(!this.isConnected()){
      throw new Error('Transport is still not connected');
    }
  }

  /*
  Send a REGISTER and wait for the registrar to actually answer it - the liveness
  probe that makes wake up recovery reliable, since a socket that survived a suspend
  reports itself as open and only a real round trip proves otherwise. It also renews
  a binding the server may have expired while we were asleep.
  */
  confirmRegistration() {

    const registerer = this.getRegisterer();

    return new Promise((resolve, reject) => {

      let settled = false;

      const finish = (error) => {
        if(settled) return;
        settled = true;
        clearTimeout(timer);
        this.removeEmitterListener(registerer.stateChange, stateListener);
        error ? reject(error) : resolve();
      };

      const timer = setTimeout(
        () => finish(new Error('The registrar did not answer our REGISTER')),
        this.registrationTimeout * 1000
      );

      const stateListener = (state) => {
        if(state === RegistererState.Registered){
          finish();
        }
        if(state === RegistererState.Terminated){
          finish(new Error('Registerer was terminated before the registrar answered'));
        }
      };

      registerer.stateChange.addListener(stateListener);

      registerer
        .register({
          ...this.registererRegisterOptions,
          requestDelegate: {
            onAccept: () => finish(),
            onReject: (response) => {
              const statusCode = response && response.message ? response.message.statusCode : undefined;
              const error = new Error(`The registrar rejected our REGISTER (${statusCode})`);
              error.registrationRejected = true;
              error.statusCode = statusCode;
              finish(error);
            }
          }
        })
        .catch((error) => {
          /*
          A refresh REGISTER can already be in flight - sip.js rejects the second
          rather than queueing it. If we are already registered that refresh is proof
          of life enough, and waiting on it would hang: stateChange only fires on a
          transition, so a refresh accepted while already Registered is silent and
          nothing settles us before the timeout throws a healthy socket away. If we
          are not registered yet, the state listener will fire, so let it.
          */
          if(error instanceof RequestPendingError){
            if(this.isRegistered()) finish();
            return;
          }
          finish(error);
        });
    });
  }

  //Return a usable registerer, building a new one if we have none or the old one was
  //terminated - a terminated registerer can never register again.
  getRegisterer() {

    if(this.registerer && this.registerer.state !== RegistererState.Terminated){
      return this.registerer;
    }

    const registererOptions = {
      ...this.registererOptions,
      expires: this.sessionExpires,
      refreshFrequency: 75
    };

    const registerer = this.createRegisterer(registererOptions);

    //sip.js refreshes on its own timer and never reports how it went, so wrap
    //register() to observe every response - otherwise a live registration is
    //indistinguishable from one that silently lapsed.
    const sendRegister = registerer.register.bind(registerer);
    registerer.register = (registerOptions = {}) => sendRegister(this.trackRegistration(registerOptions));

    registerer.stateChange.addListener((state) => {
      switch (state) {
        case RegistererState.Registered:
          this.registrationVerifiedAt = Date.now();
          this.emit('registered');
          break;
        case RegistererState.Unregistered:
          this.emit('unregistered');
          break;
        case RegistererState.Terminated:
          if(this.registerer === registerer){
            this.registerer = undefined;
          }
          break;
        default:
          break;
      }
    });

    this.registerer = registerer;
    return registerer;
  }

  //Record when the registrar last accepted us, keeping any caller delegate intact
  trackRegistration(registerOptions = {}) {

    const callerDelegate = registerOptions.requestDelegate || {};

    return {
      ...registerOptions,
      requestDelegate: {
        ...callerDelegate,
        onAccept: (response) => {
          this.registrationVerifiedAt = Date.now();
          if(callerDelegate.onAccept) callerDelegate.onAccept(response);
        }
      }
    };
  }

  //Are we deliberately holding off on re-registering after a rejection?
  registrationBlocked() {
    return !!this.registrationBlockedUntil && Date.now() < this.registrationBlockedUntil;
  }

  //Throw away the current socket, and the registerer bound to it, whenever the
  //connection we appear to have cannot be trusted.
  async recycleTransport() {

    this.disposeRegisterer();

    this.recyclingTransport = true;

    try {
      //Bounded: closing a dead socket can hang waiting for a close handshake
      await this.withTimeout(this.userAgent.transport.disconnect(), this.transportCloseTimeout, 'Transport disconnect timed out');

      //Give a late close event a moment to land before we start trusting them again
      await this.wait(0.05);
      this.recyclingTransport = false;

    } catch (error) {
      console.log('VOXOSip: could not close the old transport -', error && error.message);

      //The close never finished, so this socket can still report a disconnect at any
      //point and we have no window left to swallow it in. Replacing the agent is the
      //only way that event cannot land on the connection we are about to build.
      this.recyclingTransport = false;

      if(this.shouldDeferRebuild()){
        return;
      }

      await this.rebuildUserAgent();
    }
  }

  /*
  Give up on every live session and tell the app each one is over.

  The session stateChange listener is what normally emits 'hangup', and it cannot fire
  for a session whose transport died mid-call - the BYE never completes, so the session
  never reaches Terminated. Dropping them silently leaves the app showing a call that
  exists nowhere in the SIP layer: every action on it fails findSession(), and the
  phantom blocks new calls and inbound answering.
  */
  abandonSessions() {

    const abandoned = this.sessions;
    this.sessions = [];

    abandoned.forEach((session) => {
      try {
        this.emit('hangup', session);
      } catch (error) {
        console.log('VOXOSip: problem reporting an abandoned call -', error && error.message);
      }
    });

    /*
    'hangup' handles the per-call cleanup (tones, headset, ringing modal) but leaves the
    call widget in place, because normally the server's own call event is what clears it
    - and that event is exactly what goes missing during an outage. These calls are gone
    for good, so say so separately and let the app drop controls that can no longer do
    anything rather than making the user dismiss them by hand.
    */
    if(abandoned.length){
      this.emit('callsAbandoned', abandoned);
    }

    this.cleanupMedia();
  }

  //Drop the registerer without waiting: a dead socket never answers the unregister
  disposeRegisterer() {

    if(!this.registerer){
      return;
    }

    const registerer = this.registerer;
    this.registerer = undefined;

    try {
      const disposal = registerer.dispose();
      if(disposal && disposal.catch) disposal.catch(() => {});
    } catch (error) {
      //already gone
    }
  }

  //A rebuild terminates every live session, and media does not travel over the
  //signalling socket - so a call in progress holds it off and reconnects instead.
  //Bounded, or a socket that never comes back leaves the client mute for the call.
  shouldDeferRebuild() {

    if(!this.sessions.length){
      this.rebuildDeferredSince = 0;
      return false;
    }

    const now = Date.now();

    if(!this.rebuildDeferredSince){
      this.rebuildDeferredSince = now;
    }

    if(now - this.rebuildDeferredSince > this.rebuildDeferralLimit * 1000){
      console.log('VOXOSip: deferred the rebuild as long as we can - replacing the agent with a call still up');
      this.rebuildDeferredSince = 0;
      return false;
    }

    console.log('VOXOSip: holding off on rebuilding the user agent - a call is still up');
    return true;
  }

  //Last resort. sip.js cannot restart a stopped user agent, and a transport that
  //keeps refusing to come back is not worth nursing, so replace it entirely. The app
  //holds the wrapper, not the agent, so this is invisible to it.
  async rebuildUserAgent() {

    console.log('VOXOSip: rebuilding the user agent');

    const previous = this.userAgent;

    this.disposeRegisterer();

    //Any session that was up died with the old agent - stopping it terminates them
    this.abandonSessions();

    //Swap the replacement in before stopping the old agent. Delegate callbacks are
    //tagged with the agent that owns them, so once this.userAgent has moved on, the
    //disconnect the old socket eventually reports is ignored.
    this.userAgent = this.configureUserAgent(this.options);
    this.userAgentStarted = true;

    try {
      const stopped = previous.stop();
      if(stopped && stopped.catch) stopped.catch(() => {});
    } catch (error) {
      //nothing left to stop
    }

    await this.userAgent.start();
  }

  //Periodic health check for everything the browser never tells us about: a transport
  //that went down without a disconnect event, a registration that lapsed in a
  //background tab, and - via the gap between ticks - a machine back from sleep.
  healthCheck() {

    const now = Date.now();
    const gap = now - this.lastHealthCheck;
    this.lastHealthCheck = now;

    if(this.disposed || !this.connectRequested){
      return;
    }

    //Timers freeze while suspended, so a gap far larger than our interval means what
    //we know about the socket is stale. The threshold sits well above background tab
    //throttling (about a minute) so ordinary throttling does not force a reconnect.
    if(gap > this.sleepGapThreshold * 1000){
      console.log(`VOXOSip: ${Math.round(gap / 1000)}s timer gap - assuming we woke up with a dead socket`);
      this.recycleTransportOnNextRecovery = true;
      this.ensureHealthy('sleep-gap');
      return;
    }

    if(!this.isConnected()){
      this.ensureHealthy('transport-down');
      return;
    }

    if(!this.registrationRequested || this.registrationBlocked()){
      return;
    }

    if(!this.isRegistered()){
      this.ensureHealthy('not-registered');
      return;
    }

    //sip.js refreshes at a percentage of the expiry the *registrar* granted, not the
    //one we asked for, and bumps it itself after a 423 - so measure against what the
    //registerer settled on, with headroom for a throttled background tab.
    const grantedExpires = (this.registerer && this.registerer.expires) || this.sessionExpires;
    if(this.registrationVerifiedAt && now - this.registrationVerifiedAt > grantedExpires * this.staleRegistrationFactor * 1000){
      console.log('VOXOSip: registration has gone stale - refreshing it');
      this.ensureHealthy('registration-stale');
    }
  }

  //Exponential backoff with jitter, capped, so tabs coming back at once do not stampede
  backoffDelay(attempt) {
    const delay = Math.min(this.reconnectionDelay * Math.pow(2, attempt - 2), this.maxReconnectionDelay);
    return delay + (Math.random() * Math.min(delay, 3));
  }

  //Sleep helper for the reconnection backoff
  wait(seconds) {
    return new Promise(resolve => setTimeout(resolve, seconds * 1000));
  }

  //Reject if a promise has not settled in time, so nothing can hang the recovery loop
  withTimeout(promise, seconds, message) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(message)), seconds * 1000);
      Promise.resolve(promise).then(
        (value) => {clearTimeout(timer); resolve(value)},
        (error) => {clearTimeout(timer); reject(error)}
      );
    });
  }

  //sip.js emitters have not always exposed removeListener - never blow up over it
  removeEmitterListener(emitter, listener) {
    try {
      if(emitter && typeof emitter.removeListener === 'function'){
        emitter.removeListener(listener);
      }
    } catch (error) {
      //nothing to remove
    }
  }

  //Kept for backwards compatibility - all reconnection goes through ensureHealthy
  attemptReconnection() {
    return this.ensureHealthy('attemptReconnection');
  }

  // --------------------------------------------------------------------------------------- //


  // -------------------------------- VOXO API FUNCTIONALITY ------------------------------- //

  /*
  These items allow authenticated requests to our API to do more
  advanced softphone features like transfers, atxfer, conference merging, and dtmf
  */

  //Transfer A Call
  async transfer(sessionId, destination, quickTransferActive){
    if (quickTransferActive) {
      this.quickTransferActive = true;
    }

    destination = destination.replace(/['":/{}()[|\-.<>?_=+ \]]/g, "");

    //Get the Session For the Channel and Host Name
    const session = this.findSession(sessionId);

    if (!session) {
      throw new Error("Session Does Not Exist");
    }

    // Create sip address from destination
    const sipAddress = `sip:${destination}@sip.voxo.co`;
    const target = UserAgent.makeURI(sipAddress);
    if (!target) {
      return Promise.reject(
        new Error(`Failed to create a valid URI from "${sipAddress}"`)
      );
    }

    try {
      await session.refer(target);
    } catch (error) {
      new Error("Failed to refer to target");
    } finally {
      await this.hangup(session.uniqueId);
      this.removeSession(session.uniqueId);
      this.cleanupMedia();
    }

    this.quickTransferActive = false;
  }

  //Perform An Attended Transfer
  async attendedTransfer(sessionId, destination, callItem){

    //Get the Session For the Channel and Host Name
    const session = this.findSession(sessionId);

    if(!session) {
      throw new Error('Session Does Not Exist');
    }

    const {channel, systemname} = callItem;
    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'transferAT',
      channel,
      ext: destination,
      server:systemname
    });
  }

  //Swap Between Transfer To Party and The Party Being Transferred
  async attendedTransferSwap(sessionId, callItem) {

    //Get the Session For the Channel and Host Name
    const session = this.findSession(sessionId);

    if(!session) {
      throw new Error('Session Does Not Exist');
    }

    const {channel, systemname} = callItem;
    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'swapAtxfer',
      channel,
      server: systemname,
    });
  }

  //Merge Attended Transfer For N-Way Calling
  async attendedTransferMerge(sessionId, callItem) {

    //Get the Session For the Channel and Host Name
    const session = this.findSession(sessionId);

    if(!session) {
      throw new Error('Session Does Not Exist');
    }

    const {channel, systemname} = callItem;
    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'mergeAtxfer',
      channel,
      server: systemname
    });
  }

  //Cancel The Pending Attended Transfer
  async attendedTransferCancel(sessionId, callItem) {

    //Get the Session For the Channel and Host Name
    const session = this.findSession(sessionId);

    if(!session) {
      throw new Error('Session Does Not Exist');
    }

    const {channel, systemname} = callItem;
    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'cancelAtxfer',
      channel,
      server: systemname
    });

  }

  //Park For A Particular User
  async parkForUser(sessionId, destination, callItem) {

    //Get The Session
    const session = this.findSession(sessionId);
    if(!session){
      throw new Error('Session Does Not Exist');
    }

    const { cidNum, cidName, direction } = session;
    const { channel, systemname } = callItem;
    const {authorizationUsername} = this.options.provisionOptions;

    const tenantCode = authorizationUsername.split('-')[1];

    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'softphonePark',
      channel,
      cidname: cidName,
      cidnum: cidNum,
      direction,
      peerName: authorizationUsername,
      server: systemname,
      parkedFor: `${destination}-${tenantCode}`
    });
  }

  //Send A DTMF
  async sendDTMF(sessionId, value, callItem) {

    const session = this.findSession(sessionId);
    if(!session){
      throw new Error('Session Does Not Exist');
    }

    const options = {
      requestOptions: {
        body: {
          contentDisposition: "render",
          contentType: "application/dtmf-relay",
          content: `Signal=${value}\r\nDuration=10`,
        },
      },
    };

    session.info(options);

  }

  //Stop recording softphone
  async toggleRecording(sessionId, paused, callItem) {

    const session = this.findSession(sessionId);
    if(!session){
      throw new Error('Session Does Not Exist');
    }

    // convert to feature codes
    const code = paused ? "#0#" : "#1#";

    for (const char of code) {
      await this.sendDTMF(sessionId, char);
      // await wait(dtmfDelay);
      await new Promise(resolve => {
        setTimeout(() => {
          resolve();
        }, 15)
      });
    }

    /*const { direction } = session;
    const { channel, systemname } = callItem;

    const callDirection = direction === 'outgoing' ? 'outbound' : 'inbound';

    const state = paused ? 1 : 0;

    this.feathersClient.service('nats-actions-handler').create({
      reqType: 'muteRecording',
      channel,
      server: systemname,
      state,
      direction: callDirection
    });*/

  }

  // --------------------------------------------------------------------------------------- //

}
